import { isNull } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import GaugeChart from 'react-gauge-chart';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../../../hooks/react-redux';
import { DropdownOption } from '../../../../datasets/fields';
import { Dropdown } from '../../../../shared/dropdown/Dropdown';
import { Scrollable } from '../../../../shared/scrollable/Scrollable';
import { scopeFormatter } from '../../../../shared/table/arkTableFormatters';
import styles from '../../Analytics.module.scss';
import { OpinionCoverageAnalytics, PermittedOpinionCoverageAnalytics, getJurisdictionCoverageMapAnalytics, getOpinionAnalyticsScope, getSelectedCounterpartyCoverage, getSelectedCoverageJurisdiction, getSelectedTransactionCoverage, setJurisdictionCounterpartyCoverage, setJurisdictionTransactionCoverage } from '../../store';
import { getAvailableCounterpartyFieldsForScope, getAvailableTransactionFieldsForScope } from '../../store/utils';
import { UnauthorisedOpinion } from '../UnauthorisedOpinion';
import { UK } from '../../../../constants/opinion';

const { lightestGrey, red, amber, gold, yellowGreen, mandatory, grey } = styles;

interface PermittedInformationProps {
    opinion: PermittedOpinionCoverageAnalytics;
    counterpartyCoverage: string | null;
    transactionCoverage: string | null;
}

export const PermittedInformation: React.FC<PermittedInformationProps> = ({ opinion: { maxRating, instanceRating, counterpartyCovered, transactionCovered }, counterpartyCoverage, transactionCoverage }) => {
    const [activePercentage, setActivePercentage] = useState<number>(0);
    const selectedPercentage = useMemo(() => instanceRating / maxRating, [instanceRating, maxRating]);
    useEffect(() => {
        if (selectedPercentage !== activePercentage) {
            setActivePercentage(selectedPercentage);
        }
    }, [activePercentage, selectedPercentage]);

    const counterpartyCoverageRating = useMemo(() => {
        if (isNull(counterpartyCoverage)) {
            return { colour: grey, value: 'No value provided' };
        }
        return (isNull(counterpartyCovered) || !counterpartyCovered) ? { colour: red, value: 'No' } : { colour: mandatory, value: 'Yes' };
    }, [counterpartyCoverage, counterpartyCovered]);

    const transactionCoverageRating = useMemo(() => {
        if (isNull(transactionCoverage)) {
            return { colour: grey, value: 'No value provided' };
        }
        switch (transactionCovered) {
            case 'Yes':
                return { colour: mandatory, value: transactionCovered };
            case 'Yes - with exceptions':
                return { colour: yellowGreen, value: transactionCovered };
            case 'Unclear':
                return { colour: gold, value: transactionCovered };
            case 'No - with exceptions':
                return { colour: amber, value: transactionCovered };
            case 'No':
            case 'Not applicable':
                return { colour: red, value: transactionCovered };
            case null:
            default:
                return { colour: grey, value: 'No value provided' };
        }
    }, [transactionCovered, transactionCoverage]);

    return (
        <div className={styles.permittedOpinionWrapper}>
            <div className={styles.gaugeChartWrapper}>
                <GaugeChart
                    nrOfLevels={5}
                    colors={[mandatory, yellowGreen, gold, amber, red]}
                    needleColor={grey}
                    needleBaseColor={grey}
                    hideText
                    style={{ marginBottom: '0px' }}
                    percent={selectedPercentage}
                    animate={activePercentage !== selectedPercentage}
                    arcPadding={0.02}
                    animDelay={0}
                />
            </div>
            {!isNull(counterpartyCoverage) &&
                <div className={styles.coverageConclusionWrapper}>
                    <div className={styles.coverageConclusionLabel}>Counterparty is covered by opinion?</div>
                    <div className={styles.coverageConclusionAnswer} style={{ color: counterpartyCoverageRating.colour }}>{counterpartyCoverageRating.value}</div>
                </div>
            }
            {!isNull(transactionCoverage) &&
                <div className={styles.coverageConclusionWrapper}>
                    <div className={styles.coverageConclusionLabel}>Close out netting is effective on Transaction type?</div>
                    <div className={styles.coverageConclusionAnswer} style={{ color: transactionCoverageRating.colour }}>{transactionCoverageRating.value}</div>
                </div>
            }
        </div>
    );
};

interface ExtensiveDataProps {
    extensiveData: OpinionCoverageAnalytics[];
    counterpartyCoverage: string | null;
    transactionCoverage: string | null;
    height: number;
}

const ExtensiveData: React.FC<ExtensiveDataProps> = ({ extensiveData, height, counterpartyCoverage, transactionCoverage }) => {

    const getFieldContent = useCallback((extensiveData: OpinionCoverageAnalytics) => {
        const { isPermitted, opinionId } = extensiveData;
        if (!isPermitted) {
            return (
                <UnauthorisedOpinion opinionId={opinionId} />
            );
        }
        const permittedData = extensiveData as PermittedOpinionCoverageAnalytics;
        return (
            <PermittedInformation opinion={permittedData} counterpartyCoverage={counterpartyCoverage} transactionCoverage={transactionCoverage} />
        );
    }, [counterpartyCoverage, transactionCoverage]);

    return (
        <div className={styles.extensiveDataWrapper}>
            <Scrollable height={`${height}px`}>
                {extensiveData.map((data, i) => {
                    const { jurisdiction, scope, commissionedBy } = data;
                    const isFirstField = i === 0;
                    return (
                        <div className={styles.opinionWrapper} key={i}>
                            {!isFirstField && <div className={styles.subDivider} />}
                            <div className={styles.jurisdiction}>{jurisdiction}</div>
                            <div className={styles.scope}>Scope: {scopeFormatter(scope)}</div>
                            <div className={styles.commissionedBy}>Commissioned By: {commissionedBy}</div>
                            <div className={styles.subDivider} />
                            {getFieldContent(data)}
                        </div>
                    );
                })}
            </Scrollable>
        </div>
    );
};

interface JurisdictionCoverageDetailsProps {
    height: number;
}

export const JurisdictionCoverageDetails: React.FC<JurisdictionCoverageDetailsProps> = ({ height }) => {
    const dispatch = useAppDispatch();

    const data = useAppSelector(getJurisdictionCoverageMapAnalytics);
    const scope = useAppSelector(getOpinionAnalyticsScope);
    const counterpartyCoverage = useAppSelector(getSelectedCounterpartyCoverage);
    const transactionCoverage = useAppSelector(getSelectedTransactionCoverage);
    const inspectedJurisdiction = useAppSelector(getSelectedCoverageJurisdiction);

    const extensiveData = useMemo(() => {
        if (inspectedJurisdiction === UK) {
            return data.filter(({ jurisdiction }) => ['England & Wales', 'Scotland'].includes(jurisdiction));
        }
        return data.filter(({ jurisdiction }) => jurisdiction === inspectedJurisdiction);
    }, [data, inspectedJurisdiction]);

    const QUESTION_HEIGHT = 65;
    const DIVIDER_HEIGHT = 42;
    const extensiveDataHeight = useMemo(() => height - (QUESTION_HEIGHT + DIVIDER_HEIGHT), [height, QUESTION_HEIGHT]);

    const counterpartyCoverageValue = useMemo(() => isNull(counterpartyCoverage) ? null : { label: counterpartyCoverage, value: counterpartyCoverage }, [counterpartyCoverage]);
    const transactionCoverageValue = useMemo(() => isNull(transactionCoverage) ? null : { label: transactionCoverage, value: transactionCoverage }, [transactionCoverage]);
    const counterpartyCoverageOptions = useMemo(() => getAvailableCounterpartyFieldsForScope(scope).map(label => ({ value: label, label })), [scope]);
    const transactionCoverageOptions = useMemo(() => getAvailableTransactionFieldsForScope(scope).map(label => ({ value: label, label })), [scope]);

    const setCounterpartyCoverage = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(dropdownValue)) {
            value = (dropdownValue as DropdownOption).value;
        }
        dispatch(setJurisdictionCounterpartyCoverage(value));
    }, [dispatch]);

    const setTransactionCoverage = useCallback((dropdownValue: DropdownOption | Options<DropdownOption> | null) => {
        let value = null;
        if (!isNull(dropdownValue)) {
            value = (dropdownValue as DropdownOption).value;
        }
        dispatch(setJurisdictionTransactionCoverage(value));
    }, [dispatch]);

    const counterpartyCoverageSelect = useMemo(() => (
        <div className={styles.dropdownContent}>
            <Dropdown
                testId='jurisdiction-counterparty-coverage'
                onChange={setCounterpartyCoverage}
                value={counterpartyCoverageValue}
                options={counterpartyCoverageOptions}
                isClearable={!isNull(transactionCoverage)}
                placeholder='Select Counterparty...'
                controlBackgroundColor={lightestGrey}
            />
        </div>
    ), [counterpartyCoverageValue, counterpartyCoverageOptions, setCounterpartyCoverage, transactionCoverage]);

    const transactionCoverageSelect = useMemo(() => (
        <div className={styles.dropdownContent}>
            <Dropdown
                testId='jurisdiction-transaction-coverage'
                onChange={setTransactionCoverage}
                value={transactionCoverageValue}
                options={transactionCoverageOptions}
                isClearable={!isNull(counterpartyCoverage)}
                placeholder='Select Transaction Type...'
                controlBackgroundColor={lightestGrey}
            />
        </div>
    ), [transactionCoverageValue, transactionCoverageOptions, setTransactionCoverage, counterpartyCoverage]);

    return (
        <div className={styles.jurisdictionCoverageDetailsWrapper}>
            <div className={styles.dropdownWrapper}>
                <div className={styles.dropdownLabel}>I would like to trade a...</div>
                {transactionCoverageSelect}
            </div>
            <div className={styles.dropdownWrapper}>
                <div className={styles.dropdownLabel}>With a...</div>
                {counterpartyCoverageSelect}
            </div>
            <div className={styles.divider} />
            {extensiveData.length > 0 && <ExtensiveData extensiveData={extensiveData} height={extensiveDataHeight} counterpartyCoverage={counterpartyCoverage} transactionCoverage={transactionCoverage} />}
        </div>
    );
};
